import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import SiteHeader from '@/components/feature/SiteHeader';
import SiteFooter from '@/components/feature/SiteFooter';
import BackToTop from '@/components/feature/BackToTop';
import Breadcrumbs from '@/components/feature/Breadcrumbs';
import Reveal from '@/components/base/Reveal';
import TopicCard from '@/components/feature/TopicCard';
import useDocumentMeta from '@/hooks/useDocumentMeta';
import { gossipCategories, gossipTopics } from '@/mocks/gossip';
import type { GossipTopic } from '@/types';
import TopicsSidebar from './components/TopicsSidebar';
import TopicsToolbar from './components/TopicsToolbar';

const allTopics = gossipTopics as unknown as GossipTopic[];

const schema = [
  {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: '热门瓜料',
    description: '爆料吃瓜站内全部娱乐热点线索汇总，支持按分类筛选与关键词搜索。',
    inLanguage: 'zh-CN',
  },
  {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: '/' },
      { '@type': 'ListItem', position: 2, name: '热门瓜料', item: '/topics' },
    ],
  },
];

export default function Topics() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCat = searchParams.get('c') || 'all';
  const query = searchParams.get('q') || '';

  const [input, setInput] = useState(query);
  const [sort, setSort] = useState<'heat' | 'new'>('heat');

  useEffect(() => {
    setInput(query);
  }, [query]);

  useDocumentMeta({
    title: '热门瓜料 - 娱乐爆料线索与网友讨论汇总 | 爆料吃瓜',
    description:
      '浏览爆料吃瓜站内全部热门瓜料，涵盖恋情绯闻、影视幕后、综艺现场、音乐动态等分类，支持关键词搜索与热度/时间排序，每条记录均标注来源与证据完整度。',
    keywords: '热门瓜料,娱乐爆料,讨论区,网友观点,爆料吃瓜,吃瓜列表',
    path: '/topics',
    ogType: 'website',
    schema,
  });

  const handleSelectCategory = (slug: string) => {
    const params = new URLSearchParams(searchParams);
    if (slug === 'all') {
      params.delete('c');
    } else {
      params.set('c', slug);
    }
    setSearchParams(params);
  };

  const handleSubmitQuery = () => {
    const params = new URLSearchParams(searchParams);
    const value = input.trim();
    if (value) {
      params.set('q', value);
    } else {
      params.delete('q');
    }
    setSearchParams(params);
  };

  const handleClear = () => {
    setInput('');
    setSearchParams(new URLSearchParams());
  };

  const results = useMemo(() => {
    const keyword = query.trim().toLowerCase();
    let list = allTopics.filter((topic) => {
      const matchCat = activeCat === 'all' || topic.categorySlug === activeCat;
      if (!matchCat) return false;
      if (!keyword) return true;
      const haystack = [topic.title, topic.summary, topic.mask, topic.category, ...topic.tags]
        .join(' ')
        .toLowerCase();
      return haystack.includes(keyword);
    });
    list = [...list].sort((a, b) =>
      sort === 'heat' ? b.heat - a.heat : b.date.localeCompare(a.date)
    );
    return list;
  }, [activeCat, query, sort]);

  const activeCategory = gossipCategories.find((cat) => cat.slug === activeCat) ?? gossipCategories[0];

  return (
    <div className="flex min-h-screen w-full flex-col bg-background-50">
      <SiteHeader />

      <main className="flex-1">
        <section className="w-full border-b border-background-200 bg-background-100">
          <div className="mx-auto w-full px-4 py-8 md:px-6 md:py-11">
            <Breadcrumbs items={[{ label: '热门瓜料' }]} />
            <h1 className="mt-4 font-heading text-3xl font-black leading-tight text-foreground-950 md:text-[40px]">
              热门瓜料
            </h1>
            <p className="mt-3 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-[15px]">
              当前正在跟踪 <strong className="text-foreground-900">{allTopics.length}</strong> 条记录。
              {activeCategory.desc}。点击任意条目可查看完整时间线、来源标注与评论区讨论。
            </p>
          </div>
        </section>

        <div className="mx-auto w-full px-4 py-8 md:px-6 md:py-10">
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-[250px_1fr] lg:gap-8">
            <TopicsSidebar activeCat={activeCat} onSelectCategory={handleSelectCategory} />

            <div>
              <TopicsToolbar
                input={input}
                onInputChange={setInput}
                onSubmit={handleSubmitQuery}
                sort={sort}
                onSortChange={setSort}
                total={results.length}
                hasQuery={Boolean(query) || activeCat !== 'all'}
                onClear={handleClear}
              />

              <div className="mt-4 flex flex-wrap items-center gap-2">
                <span className="text-xs text-foreground-500">当前分类：</span>
                <span className="rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-primary-700">
                  {activeCategory.name}
                </span>
                {query && (
                  <span className="rounded-full bg-accent-100 px-3 py-1 text-xs font-medium text-accent-900">
                    关键词「{query}」
                  </span>
                )}
              </div>

              {results.length > 0 ? (
                <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2">
                  {results.map((topic, index) => (
                    <Reveal key={topic.id} delay={index * 50}>
                      <div className="h-full">
                        <TopicCard topic={topic} />
                      </div>
                    </Reveal>
                  ))}
                </div>
              ) : (
                <div className="mt-6 rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-16 text-center">
                  <i className="ri-search-eye-line text-3xl text-foreground-300" aria-hidden="true" />
                  <p className="mt-3 font-heading text-base font-bold text-foreground-900">没有匹配的瓜料</p>
                  <p className="mt-1.5 text-sm text-foreground-600">
                    换个关键词试试，或者清除筛选查看全部记录。
                  </p>
                  <button
                    type="button"
                    onClick={handleClear}
                    className="mt-5 cursor-pointer rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
                  >
                    查看全部瓜料
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      <SiteFooter />
      <BackToTop />
    </div>
  );
}